import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, CreditCard, ArrowRight, DollarSign, Info, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { friendlyError } from "@/lib/errors";

/**
 * /case/:id/pay        — Show quote breakdown + "Pay with Stripe" button
 * /payment/success     — Poll payment status until paid; then link back to case
 * /payment/cancel      — Friendly cancel screen with a "try again" back-link
 */

const money = (c) => `$${(c / 100).toFixed(2)}`;

function uidApplyLabel(saving, existing) {
  if (saving) return "Applying…";
  return existing ? "Update" : "Apply";
}

function FeeWaivedScreen({ quote, busy, onContinue, caseId }) {
  return (
    <AppShell>
      <div className="mx-auto max-w-lg px-6 py-14 text-center" data-testid="payment-checkout-page">
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
          style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
        >
          <CheckCircle2 className="w-7 h-7" style={{ color: "var(--ai-text)" }} />
        </div>
        <h1 className="font-display text-3xl font-extrabold mb-2" data-testid="payment-waived-heading">
          Fee waived — you&apos;re all set 🎁
        </h1>
        <p className="text-sm mb-2" style={{ color: "var(--text-secondary)" }}>
          An administrator has waived the preparation fee for this case
          {quote.fee_waived_reason ? " because: " : "."}
          {quote.fee_waived_reason && <em>&ldquo;{quote.fee_waived_reason}&rdquo;</em>}
        </p>
        <p className="text-xs mb-6" style={{ color: "var(--text-secondary)" }}>
          The regular price would have been {money(quote.original_total_cents || 0)}.
          Government fees ({money(quote.uscis_filing_fee_cents)} IV fee per applicant) are paid separately — and only — at ceac.state.gov.
        </p>
        <button
          onClick={onContinue}
          disabled={busy}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="payment-continue-waived-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {busy ? "One moment…" : "Continue to my packet"}
        </button>
        <div className="mt-4">
          <Link to={`/case/${caseId}`} className="btn-ghost text-sm">Back to case</Link>
        </div>
      </div>
    </AppShell>
  );
}

function ReferralUidBlock({ uidInput, setUidInput, applyUid, uidSaving, caseRefUid, uidErr, uidLookup, ffDiscountCents }) {
  return (
    <div className="card-surface p-4 mb-4" data-testid="checkout-uid-block">
      <div className="text-xs font-semibold uppercase tracking-wide mb-2"
           style={{ color: "var(--text-secondary)" }}>
        Have a referral UID?
      </div>
      <div className="flex flex-col md:flex-row md:items-start gap-2">
        <input className="field-input font-mono text-sm md:flex-1" value={uidInput}
               onChange={(e) => setUidInput(e.target.value)}
               placeholder="e.g. FF-10001 or AFF-10001"
               data-testid="checkout-uid-input" />
        <button type="button" className="btn-primary text-sm" onClick={applyUid}
                disabled={uidSaving} data-testid="checkout-uid-apply">
          {uidApplyLabel(uidSaving, caseRefUid)}
        </button>
        {caseRefUid && (
          <button type="button" className="btn-ghost text-sm"
                  onClick={() => { setUidInput(""); setTimeout(applyUid, 0); }}
                  data-testid="checkout-uid-clear">Remove</button>
        )}
      </div>
      {uidErr && (
        <div className="mt-2 text-xs p-2 rounded" style={{ background: "#FEF2F2", color: "#991B1B" }}
             data-testid="checkout-uid-error">{uidErr}</div>
      )}
      {uidLookup && uidLookup.valid && uidLookup.program === "friend_family" && ffDiscountCents > 0 && (
        <div className="mt-2 text-xs p-2 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}
             data-testid="checkout-uid-discount">
          {money(ffDiscountCents)} Friend &amp; Family discount will be applied at checkout.
        </div>
      )}
      {uidLookup && uidLookup.valid && uidLookup.program === "affiliate" && (
        <div className="mt-2 text-xs p-2 rounded" style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
             data-testid="checkout-uid-affiliate">
          Affiliate UID attached. No customer discount applies to affiliate referrals.
        </div>
      )}
      {uidInput && uidLookup && !uidLookup.valid && (
        <div className="mt-2 text-xs p-2 rounded" style={{ background: "#FEF2F2", color: "#991B1B" }}>
          That UID is not valid. Leave blank to continue without a referral.
        </div>
      )}
    </div>
  );
}

export function PaymentCheckoutPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [creditCents, setCreditCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Referral UID entry — Canonical Spec v1.2 §4 (UID is the sole authoritative
  // attribution mechanism). Pre-populated from ?ref= query as UX courtesy.
  const [uidInput, setUidInput] = useState("");
  const [uidLookup, setUidLookup] = useState(null);
  const [uidSaving, setUidSaving] = useState(false);
  const [uidErr, setUidErr] = useState("");
  const [ffDiscountCents, setFfDiscountCents] = useState(0);
  const [caseRefUid, setCaseRefUid] = useState("");

  const loadQuote = useCallback(async () => {
    setLoading(true);
    try {
      const [q, r, caseResp] = await Promise.all([
        api.get(`/pricing/quote?case_id=${id}`),
        api.get("/referrals/summary").catch(() => ({ data: null })),
        api.get(`/cases/${id}`).catch(() => ({ data: null })),
      ]);
      setQuote(q.data);
      setCreditCents(r.data?.available_credit_cents || 0);
      const savedUid = caseResp.data?.referral_uid || "";
      if (savedUid) {
        setCaseRefUid(savedUid);
        setUidInput(savedUid);
        // Look up the discount for display
        try {
          const { data: lookup } = await api.get(`/refer/lookup?uid=${encodeURIComponent(savedUid)}`);
          setUidLookup(lookup);
          setFfDiscountCents(lookup.valid ? (lookup.discount_cents || 0) : 0);
        } catch (err) {
          // Non-critical: UID lookup failed, discount just won't show. Log but don't block checkout.
          console.warn("[PaymentPages] Referral UID lookup failed:", err);
        }
      } else {
        // UX courtesy: pre-populate from ?ref=  in URL (never auto-attach).
        const url = new URL(window.location.href);
        const qref = (url.searchParams.get("ref") || "").trim().toUpperCase();
        if (qref) setUidInput(qref);
      }
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load the pricing for this case."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const applyUid = async () => {
    setUidErr("");
    setUidSaving(true);
    try {
      const raw = uidInput.trim().toUpperCase();
      await api.post(`/cases/${id}/referral-uid`, { uid: raw || null });
      if (raw) {
        const { data: lookup } = await api.get(`/refer/lookup?uid=${encodeURIComponent(raw)}`);
        setUidLookup(lookup);
        setFfDiscountCents(lookup.valid ? (lookup.discount_cents || 0) : 0);
        setCaseRefUid(lookup.valid ? raw : "");
      } else {
        setUidLookup(null);
        setFfDiscountCents(0);
        setCaseRefUid("");
      }
    } catch (e) {
      setUidErr(e.response?.data?.detail || e.message);
    } finally {
      setUidSaving(false);
    }
  };

  useEffect(() => { loadQuote(); }, [loadQuote]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        case_id: id,
        origin_url: window.location.origin,
      });
      // For waived cases the backend returns a synthetic checkout URL that
      // points at /payment/success — same redirect UX, no Stripe involved.
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't start checkout. Please try again."));
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-6 py-14 flex items-center gap-3 text-sm" style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your quote…
        </div>
      </AppShell>
    );
  }
  if (!quote) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-6 py-14 text-sm">
          We couldn't load pricing for this case.{" "}
          <button className="text-[var(--action)] underline" onClick={() => nav(`/case/${id}`)}>Back to your case</button>
        </div>
      </AppShell>
    );
  }

  // Fee-waived cases display a specialized "you're comp'd" screen with a
  // single "Continue to my packet" CTA instead of a real Stripe checkout.
  if (quote.fee_waived) {
    return <FeeWaivedScreen quote={quote} busy={busy} onContinue={startCheckout} caseId={id} />;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl px-6 py-10" data-testid="payment-checkout-page">
        <div className="pill pill-ai mb-3 inline-flex">Ready to file</div>
        <h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
          Your preparation fee
        </h1>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          Government NVC/IV fees are paid separately — and only — at ceac.state.gov. Never mail a payment.
        </p>

        <div className="card-surface p-6 mb-4" data-testid="payment-quote-breakdown">
          {quote.classification?.primary_category && (
            <div
              className="mb-4 pb-4 border-b flex items-start gap-2 text-xs"
              style={{ borderColor: "var(--border-stone)", color: "var(--text-secondary)" }}
              data-testid="payment-category-explainer"
            >
              <ScrollText className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--accent-copper)" }} />
              <div>
                Petition category: <strong style={{ color: "var(--text-primary)" }}>{quote.classification.primary_category}</strong>.{" "}
                {quote.classification.primary?.reason}
              </div>
            </div>
          )}
          <ul className="text-sm space-y-2">
            {quote.line_items.map((li, idx) => (
              <li key={`${li.label}-${idx}`} className="flex justify-between items-start gap-3" style={{ color: "var(--text-primary)" }}>
                <span className="flex items-start gap-1.5">
                  {li.reason && (
                    <span
                      className="cursor-help"
                      title={li.reason}
                      data-testid={`payment-line-info-${idx}`}
                    >
                      <Info className="w-3.5 h-3.5 mt-0.5" style={{ color: "var(--accent-copper)" }} />
                    </span>
                  )}
                  <span>{li.label}</span>
                </span>
                <span>{money(li.cents)}</span>
              </li>
            ))}
            <li className="flex justify-between font-semibold pt-3 border-t" style={{ borderColor: "var(--border-stone)" }}>
              <span>Subtotal</span>
              <span>{money(quote.subtotal_cents)}</span>
            </li>
            {quote.surcharge_cents > 0 && (
              <li className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
                <span>+ {quote.surcharge_pct}% card processing surcharge</span>
                <span>{money(quote.surcharge_cents)}</span>
              </li>
            )}
            {creditCents > 0 && (() => {
              // Apply credit up to (total - $0.50 dust floor to keep Stripe happy)
              const applied = Math.min(creditCents, Math.max(0, quote.total_cents - 50));
              if (applied <= 0) return null;
              const finalDue = quote.total_cents - applied;
              return (
                <>
                  <li
                    className="flex justify-between text-sm"
                    data-testid="payment-referral-credit-line"
                    style={{ color: "var(--success-text)" }}
                  >
                    <span>Referral credit applied</span>
                    <span>− {money(applied)}</span>
                  </li>
                  <li
                    className="flex justify-between font-bold text-lg pt-3 border-t"
                    style={{ borderColor: "var(--border-stone)", color: "var(--action)" }}
                  >
                    <span>Total due today</span>
                    <span data-testid="payment-total">{money(finalDue)}</span>
                  </li>
                </>
              );
            })()}
            {(creditCents === 0 || Math.min(creditCents, Math.max(0, quote.total_cents - ffDiscountCents - 50)) === 0) && (
              <li className="flex justify-between font-bold text-lg pt-3 border-t" style={{ borderColor: "var(--border-stone)", color: "var(--action)" }}>
                <span>Total due today</span>
                <span data-testid="payment-total">{money(Math.max(50, quote.total_cents - ffDiscountCents))}</span>
              </li>
            )}
            {ffDiscountCents > 0 && (
              <li className="flex justify-between text-sm"
                  data-testid="payment-ff-discount-line"
                  style={{ color: "var(--success-text)" }}>
                <span>Friend &amp; Family discount</span>
                <span>− {money(ffDiscountCents)}</span>
              </li>
            )}
          </ul>
          <div className="mt-4 text-xs flex items-start gap-2 p-3 rounded-md" style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}>
            <DollarSign className="w-4 h-4 flex-shrink-0" />
            <span>
              Separately, the government charges an IV fee of{" "}
              <strong>{money(quote.uscis_filing_fee_cents)}</strong> per applicant, paid online at ceac.state.gov.
              That's included in your packet — we don't collect it.
            </span>
          </div>
        </div>

        {quote.surcharge_disclosure && (
          <p className="text-[11px] mb-4" style={{ color: "var(--text-secondary)" }}>
            {quote.surcharge_disclosure}
          </p>
        )}

        {/* Refund policy disclosure — Canonical Spec v1.3 §7 */}
        <div className="mb-4 p-3 rounded-md text-xs"
             style={{ background: "var(--bg-cream)", border: "1px solid var(--border-stone)", color: "var(--text-secondary)" }}
             data-testid="checkout-refund-policy">
          <strong style={{ color: "var(--text-primary)" }}>Refund policy:</strong>{" "}
          The Free Plan lets you evaluate the platform at no cost before you pay. Because
          the Prepare &amp; File package is immediately usable work product, fees are
          non-refundable once the completed package has been generated. Duplicate payments,
          technical failures, or administrative errors are eligible for review at our
          sole discretion &mdash; contact{" "}
          <a href="mailto:support@ds-260usa.com" className="underline">
            support@ds-260usa.com
          </a>. See our full{" "}
          <a href="/legal/terms" target="_blank" rel="noreferrer" className="underline">
            Terms of Service &sect;7
          </a>.
        </div>

        {/* Referral UID entry — Canonical Spec v1.2 §4 */}
        <ReferralUidBlock
          uidInput={uidInput}
          setUidInput={setUidInput}
          applyUid={applyUid}
          uidSaving={uidSaving}
          caseRefUid={caseRefUid}
          uidErr={uidErr}
          uidLookup={uidLookup}
          ffDiscountCents={ffDiscountCents}
        />

        <div className="flex items-center gap-3">
          <button
            className="btn-primary flex items-center gap-2"
            onClick={startCheckout}
            disabled={busy}
            data-testid="payment-checkout-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {busy ? "Redirecting to Stripe…" : `Pay ${money(Math.max(50, quote.total_cents - ffDiscountCents - Math.min(creditCents, Math.max(0, quote.total_cents - ffDiscountCents - 50))))} with Stripe`}
          </button>
          <Link to={`/case/${id}`} className="btn-ghost text-sm">Back to case</Link>
        </div>
        <p className="text-[11px] mt-3" style={{ color: "var(--text-secondary)" }}>
          Powered by Stripe — you'll be redirected to a secure Stripe-hosted checkout page.
        </p>
      </div>
    </AppShell>
  );
}

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState({ phase: "loading" });

  useEffect(() => {
    if (!sessionId) {
      setStatus({ phase: "error", msg: "Missing session_id" });
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ phase: "paid", caseId: data.case_id, amount: data.amount_cents });
          toast.success("Payment received — your case is ready to file!");
          // Fire the referral prompt (globally mounted in App.js). Only shows
          // once per case_id (persisted in localStorage). This is when the
          // referral credit is actually earned (referee's first payment).
          if (data.case_id) {
            window.dispatchEvent(new CustomEvent("gcu:payment-paid",
              { detail: { caseId: data.case_id, sessionId } }));
          }
          return;
        }
        if (attempts >= 10) {
          setStatus({ phase: "pending", caseId: data.case_id });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        setStatus({ phase: "error", msg: friendlyError(err) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <AppShell>
      <div className="mx-auto max-w-lg px-6 py-14 text-center" data-testid="payment-success-page">
        {status.phase === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--action)" }} />
            <h1 className="font-display text-2xl font-bold mb-2">Confirming your payment…</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Hang tight, this only takes a couple of seconds.</p>
          </>
        )}
        {status.phase === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#065F46" }} />
            <h1 className="font-display text-3xl font-extrabold mb-2">Payment received</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
              Thank you! Your case preparation fee has been paid. You can now download your final filing packet.
            </p>
            {status.caseId && (
              <button
                className="btn-primary inline-flex items-center gap-2"
                onClick={() => nav(`/case/${status.caseId}`)}
                data-testid="payment-back-to-case"
              >
                Back to my case <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </>
        )}
        {status.phase === "pending" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--action)" }} />
            <h1 className="font-display text-2xl font-bold mb-2">Payment still processing</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
              This can occasionally take a minute or two. You can safely close this window — we'll email you when it's confirmed.
            </p>
            {status.caseId && (
              <Link to={`/case/${status.caseId}`} className="btn-ghost text-sm">Back to my case</Link>
            )}
          </>
        )}
        {status.phase === "error" && (
          <>
            <XCircle className="w-14 h-14 mx-auto mb-4" style={{ color: "#991B1B" }} />
            <h1 className="font-display text-2xl font-bold mb-2">We couldn't confirm your payment</h1>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{status.msg}</p>
            <Link to="/dashboard" className="btn-ghost text-sm">Back to your dashboard</Link>
          </>
        )}
      </div>
    </AppShell>
  );
}

export function PaymentCancelPage() {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get("case_id");
  return (
    <AppShell>
      <div className="mx-auto max-w-lg px-6 py-14 text-center" data-testid="payment-cancel-page">
        <XCircle className="w-14 h-14 mx-auto mb-4" style={{ color: "var(--text-secondary)" }} />
        <h1 className="font-display text-2xl font-bold mb-2">Checkout canceled</h1>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          No charge was made. Your case is still saved — come back anytime to complete payment.
        </p>
        {caseId ? (
          <Link to={`/case/${caseId}/pay`} className="btn-primary inline-flex items-center gap-2">
            Try again <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link to="/dashboard" className="btn-ghost text-sm">Back to your dashboard</Link>
        )}
      </div>
    </AppShell>
  );
}
